import moment from "moment";
import { parseCSV } from "./fileUtils";
import { normalizeKey } from "./prefillUtils";
import { emailRegex } from "../constant/const";
import {
  changeDateToMomentFormat,
  radioButtonWidget,
  generateId
} from "../constant/Utils";

// Widget types that cannot be imported through CSV (see OpenSign docs).
const UNSUPPORTED_CSV_TYPES = new Set([
  "image",
  "draw",
  "signature",
  "stamp",
  "initials"
]);
export const BULK_CSV_DATE_FORMAT = "DD-MM-YYYY";
export const BULK_CSV_MAX_DOCS = 1000;

const sampleValue = (header, widget) => {
  if (header.type === "role") return "name@example.com";
  switch (widget?.type) {
    case "date":
      return "28-01-2026";
    case "checkbox":
      return (widget?.options?.values || []).slice(0, 2).join("|");
    case "dropdown":
    case radioButtonWidget:
      return widget?.options?.values?.[0] || "";
    case "number":
      return "1";
    default:
      return "Sample";
  }
};

const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Maximum number of documents for one Bulk Send: 1000 divided by the roles.
export const getBulkCsvLimit = (roleCount = 1) =>
  Math.max(1, Math.floor(BULK_CSV_MAX_DOCS / Math.max(1, roleCount)));

// Keep the traditional recipient label unless it conflicts with a widget.
export const buildBulkHeaders = (fields = []) => {
  const widgetHeaders = fields.flatMap((role) => (role.widgets || []).map((widget) => ({
    label: widget.label,
    role: role.label,
    type: widget.type,
    isRequired: false
  })));
  const used = new Set(widgetHeaders.map((header) => header.label.toLowerCase()));
  const emailHeaders = fields.map((role) => {
    let label = `${role.label}::Email`;
    if (used.has(label.toLowerCase())) {
      const base = `${role.label}::Recipient Email`;
      label = base;
      let suffix = 2;
      while (used.has(label.toLowerCase())) label = `${base} ${suffix++}`;
    }
    used.add(label.toLowerCase());
    return { label, role: role.label, type: "role", isRequired: true };
  });
  return [...emailHeaders, ...widgetHeaders];
};

// Builds sample CSV text. Columns are exactly the table headers.
export const buildSampleCsv = (headers = [], fields = []) => {
  const widgetByLabel = new Map();
  fields.forEach((f) => (f.widgets || []).forEach((w) => widgetByLabel.set(w.label, w)));
  const exportable = headers.filter((h) => !UNSUPPORTED_CSV_TYPES.has(h.type));
  const head = exportable.map((h) => csvCell(h.label)).join(",");
  const row = exportable
    .map((h) => csvCell(sampleValue(h, widgetByLabel.get(h.label))))
    .join(",");
  return `${head}\n${row}\n`;
};

export const downloadTextFile = (text, filename) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Converts one CSV cell to the value stored in widget.response.
// Returns { value } or { error }.
const convertCell = (widget, raw) => {
  const text = (raw ?? "").trim();
  if (!text) return { value: "" };
  const opts = (widget?.options?.values || []).map((v) => String(v).trim());
  switch (widget.type) {
    case "date": {
      if (text.toLowerCase() === "today") {
        const fmt = widget?.options?.validation?.format || "MM/dd/yyyy";
        return { value: moment().format(changeDateToMomentFormat(fmt)) };
      }
      const m = moment(text, BULK_CSV_DATE_FORMAT, true);
      if (!m.isValid()) return { error: `date must be ${BULK_CSV_DATE_FORMAT}` };
      const fmt = widget?.options?.validation?.format || "MM/dd/yyyy";
      return { value: m.format(changeDateToMomentFormat(fmt)) };
    }
    case "checkbox": {
      const picked = text.split("|").map((s) => s.trim()).filter(Boolean);
      const idx = [];
      for (const p of picked) {
        const i = opts.findIndex((o) => o.toLowerCase() === p.toLowerCase());
        if (i < 0) return { error: `"${p}" is not an option` };
        if (!idx.includes(i)) idx.push(i);
      }
      return { value: idx };
    }
    case "dropdown":
    case radioButtonWidget: {
      const i = opts.findIndex((o) => o.toLowerCase() === text.toLowerCase());
      if (i < 0) return { error: `"${text}" is not an option` };
      return { value: opts[i] };
    }
    default:
      return { value: text };
  }
};

/**
 * Parses CSV text and builds Bulk Send form rows.
 * @param {string} text         CSV file content
 * @param {Array} headers       table headers ({label, role, type})
 * @param {Array} baseFields    signer fields (role + widgets) used as row template
 * @param {Array} reservedEmails emails that already belong to the template
 * @returns {{ forms: Array, errors: string[] }}
 */
export const buildFormsFromCsv = (
  text,
  headers = [],
  baseFields = [],
  reservedEmails = []
) => {
  const errors = [];
  const rows = parseCSV((text || "").replace(/^\uFEFF/, ""));
  if (rows.length < 2) {
    return { forms: [], errors: ["The file has no data rows."] };
  }
  const csvHeaders = rows[0];
  const known = new Map(headers.map((h) => [h.label.toLowerCase(), h]));
  const colIndex = new Map(); // header label -> column index
  csvHeaders.forEach((h, i) => {
    const key = (h || "").trim().toLowerCase();
    if (known.has(key)) colIndex.set(known.get(key).label, i);
    else if (key) errors.push(`Unknown column "${h}". Do not change column names.`);
  });
  headers
    .filter((h) => h.type === "role")
    .forEach((h) => {
      if (!colIndex.has(h.label)) errors.push(`Missing column "${h.label}".`);
    });
  if (errors.length) return { forms: [], errors };

  const limit = getBulkCsvLimit(baseFields.length);
  const dataRows = rows.slice(1);
  if (dataRows.length > limit) {
    return {
      forms: [],
      errors: [
        `Too many rows: ${dataRows.length}. The limit is ${limit} documents for ${baseFields.length} role(s).`
      ]
    };
  }

  const reserved = new Set(reservedEmails.map(normalizeKey));
  const forms = [];
  dataRows.forEach((cells, r) => {
    const rowNo = r + 1;
    const seen = new Set();
    const fields = baseFields.map((field) => {
      const roleHeader = headers.find((h) => h.type === "role" && h.role === field.label);
      const emailRaw = cells[colIndex.get(roleHeader?.label)] || "";
      const email = normalizeKey(emailRaw);
      if (!email) errors.push(`Row ${rowNo}: email for "${field.label}" is empty.`);
      else if (!emailRegex.test(email))
        errors.push(`Row ${rowNo}: invalid email "${emailRaw}".`);
      else if (seen.has(email) || reserved.has(email))
        errors.push(`Row ${rowNo}: duplicate email "${email}".`);
      seen.add(email);

      const widgets = (field.widgets || []).map((w) => {
        if (UNSUPPORTED_CSV_TYPES.has(w.type) || !colIndex.has(w.label)) return w;
        const { value, error } = convertCell(w, cells[colIndex.get(w.label)]);
        if (error) {
          errors.push(`Row ${rowNo}, "${w.label}": ${error}.`);
          return w;
        }
        return { ...w, options: { ...w.options, response: value }, response: value };
      });
      return { ...field, email, signer: "", widgets };
    });
    forms.push({ Id: generateId(8), fields });
  });
  return { forms: errors.length ? [] : forms, errors };
};
